import React from 'react';
import _ from 'lodash';

import {
  Box,
  Modal,
  Typography,
  Button
} from '@mui/material';

import { Close } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { LoadingOverlay } from '../../layer0';



interface IContainerBoxProps {
  width?: string;
  isDoubleWide: boolean;
  expandWidthToFit: boolean;
  fullHeight: boolean;
}

const ContainerBox = styled(Box, {
  shouldForwardProp: prop =>
    prop !== 'width' &&
    prop !== 'isDoubleWide' &&
    prop !== 'expandWidthToFit' &&
    prop !== 'fullHeight'
})<IContainerBoxProps>(({
  theme,
  width,
  isDoubleWide,
  expandWidthToFit,
  fullHeight
}) => {
  let finalWidth: string | undefined = undefined;
  if (!_.isUndefined(width)) {
    finalWidth = width;
  } else if (!expandWidthToFit) {
    finalWidth = theme.spacing(57 * (isDoubleWide ? 2 : 1));
  }

  return {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    borderRadius: 10,
    width: finalWidth,
    maxWidth: '90vw',
    maxHeight: '90vh',
    height: fullHeight ? '90vh' : undefined,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch'
  };
});

const HeaderDiv = styled('div')(({theme}) => ({
  paddingTop: theme.spacing(1.5),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(3),
  borderBottom: '1px solid #EEEDF0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const TitleContainerDiv = styled('div')({
  display: 'flex',
  alignItems: 'center'
});

const TitleIconContainerDiv = styled('div')(({theme}) => ({
  marginRight: theme.spacing(1)
}));

interface ITitleTypographyProps {
  color?: string;
}

const TitleTypography = styled(Typography, {
  shouldForwardProp: prop =>
    prop !== 'color'
})<ITitleTypographyProps>(({color}) => ({
  fontWeight: 500,
  fontSize: '20px',
  color: color ?? '#263238'
}));

interface IBodyDivProps {
  canScroll?: boolean;
  disableDefaultPadding?: boolean;
}

const BodyDiv = styled('div', {
  shouldForwardProp: prop =>
    prop !== 'canScroll' &&
    prop !== 'disableDefaultPadding'
})<IBodyDivProps>(({theme, canScroll, disableDefaultPadding}) => ({
  flex: '1 0',
  padding: !disableDefaultPadding ?
    theme.spacing(3) :
    undefined,
  paddingBottom: !disableDefaultPadding ?
    0 :
    undefined,
  overflowY: canScroll ? 'auto' : undefined,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch'
}));

const ButtonRowBox = styled(Box)(({theme}) => ({
  borderTop: '1px solid #EEEDF0',
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center'
}));

const ButtonContainerBox = styled(Box)(({theme}) => ({
  display: 'flex',
  marginLeft: theme.spacing(1)
}));



export interface IFnGenElTopCloseButtonArgs {
  onClose?: () => void;
  isLoading: boolean;
}

export interface IFnGenElCloseButtonArgs {
  onClose?: () => void;
  isLoading: boolean;
  cancelButtonLabel?: string;
}

export interface IFnGenElAcceptButtonArgs {
  onAccept?: () => void;
  isLoading: boolean;
  acceptButtonLabel?: string;
}

export interface IChildrenArgs {
  BodyComponent: typeof BodyDiv;
  ButtonRowComponent: typeof ButtonRowBox;
  ButtonContainerComponent: typeof ButtonContainerBox;
  buttons?: JSX.Element;
}

export interface IProps {
  elTitleIcon?: JSX.Element;
  title?: string;
  titleColor?: string;
  isOpen: boolean;
  isLoading?: boolean;
  onAccept?: () => void;
  onClose?: () => void;
  acceptButtonLabel?: string;
  cancelButtonLabel?: string;
  disableDefaultWidth?: boolean;
  showButtons?: boolean;
  hideAcceptButton?: boolean;
  hideCloseButton?: boolean;
  width?: string;
  isDoubleWide?: boolean;
  expandWidthToFit?: boolean;
  fullHeight?: boolean;
  hideTopCloseButton?: boolean;
  fnGenElTopCloseButton?: (args: IFnGenElTopCloseButtonArgs) => React.ReactNode;
  fnGenElCloseButton?: (args: IFnGenElCloseButtonArgs) => React.ReactNode;
  fnGenElAcceptButton?: (args: IFnGenElAcceptButtonArgs) => React.ReactNode;
  children: (args: IChildrenArgs) => React.ReactNode;
}

export const FC: React.FC<IProps> = props => {
  const {
    isLoading,
    onClose,
    fnGenElTopCloseButton
  } = props;
  const elTopCloseButton = React.useMemo(
    () => fnGenElTopCloseButton?.({
      onClose,
      isLoading: !!isLoading
    }) ?? (
      <Button
        color="secondary"
        onClick={onClose}
        disabled={isLoading}>
        <Close />
      </Button>
    ),
    [
      isLoading,
      onClose,
      fnGenElTopCloseButton
    ]
  );



  const {
    cancelButtonLabel,
    fnGenElCloseButton
  } = props;
  const elCloseButton = React.useMemo(
    () => fnGenElCloseButton?.({
      onClose,
      isLoading: !!isLoading,
      cancelButtonLabel
    }) ?? (
      <Button
        onClick={onClose}
        fullWidth
        color="secondary"
        disabled={isLoading}>
        {cancelButtonLabel ?? "Cancel"}
      </Button>
    ),
    [
      isLoading,
      onClose,
      cancelButtonLabel,
      fnGenElCloseButton
    ]
  );



  const {
    acceptButtonLabel,
    onAccept,
    fnGenElAcceptButton
  } = props;
  const elAcceptButton = React.useMemo(
    () => fnGenElAcceptButton?.({
      onAccept,
      isLoading: !!isLoading,
      acceptButtonLabel
    }) ?? (
      <Button
        type="submit"
        onClick={onAccept}
        fullWidth
        variant="contained"
        disabled={isLoading}>
        {acceptButtonLabel ?? "Accept"}
      </Button>
    ),
    [
      isLoading,
      onAccept,
      acceptButtonLabel,
      fnGenElAcceptButton
    ]
  );



  return (
    <Modal
      open={props.isOpen}
      onClose={props.onClose ?? (() => {})}>

      <>
        <LoadingOverlay.FC
          active={!!props.isLoading} />

        <ContainerBox
          width={props.width}
          isDoubleWide={!!props.isDoubleWide}
          expandWidthToFit={!!props.expandWidthToFit}
          fullHeight={!!props.fullHeight}
          style={{
            width: props.disableDefaultWidth ? 'unset' : undefined
          }}>

          {props.title && (
            <HeaderDiv>
              <TitleContainerDiv>
                {props.elTitleIcon && (
                  <TitleIconContainerDiv>
                    {props.elTitleIcon}
                  </TitleIconContainerDiv>
                )}

                <TitleTypography
                  color={props.titleColor}>
                  {props.title}
                </TitleTypography>
              </TitleContainerDiv>

              {!props.hideTopCloseButton &&
                elTopCloseButton}
            </HeaderDiv>
          )}

          {props.children({
            BodyComponent: BodyDiv,
            ButtonRowComponent: ButtonRowBox,
            ButtonContainerComponent: ButtonContainerBox,
            buttons: (props.showButtons && (!props.hideAcceptButton || !props.hideCloseButton)) ? (
              <ButtonRowBox>
                {!props.hideCloseButton && (
                  <ButtonContainerBox>
                    {elCloseButton}
                  </ButtonContainerBox>
                )}

                {!props.hideAcceptButton && (
                  <ButtonContainerBox>
                    {elAcceptButton}
                  </ButtonContainerBox>
                )}
              </ButtonRowBox>
            ) : undefined
          })}

        </ContainerBox>
      </>

    </Modal>
  );
};
